import * as React from 'react'
import {useState, useEffect} from 'react'
import {
  DataGridPro,
  GridToolbar,
  GridSortDirection,
  GridSortModel,
  GridAlignment,
} from '@mui/x-data-grid-pro'
import axios from 'axios'

import IconButton from '@mui/material/IconButton'
import './BILAwards.css'
import {createTheme, ThemeProvider} from '@mui/material/styles'
import VeryHighIcon from '@mui/icons-material/ArrowUpward' // Example icon for Very High
import HighIcon from '@mui/icons-material/TrendingUp' // Example icon for High
import MediumIcon from '@mui/icons-material/Remove' // Example icon for Medium
import LowIcon from '@mui/icons-material/TrendingDown' // Example icon for Low
import VeryLowIcon from '@mui/icons-material/ArrowDownward' // Example icon for Very Low
import Chip from '@mui/material/Chip'
import {Button, Card, CardContent, Paper, Typography} from '@material-ui/core'
import {Link} from 'react-router-dom'
import ErrorIcon from '@mui/icons-material/Error' // Critical Need
import WarningIcon from '@mui/icons-material/Warning' // High Need
import InfoIcon from '@mui/icons-material/Info' // Moderate Need
import CheckCircleIcon from '@mui/icons-material/CheckCircle' // Low Need
import DoneAllIcon from '@mui/icons-material/DoneAll' // Adequate
import VerifiedIcon from '@mui/icons-material/Verified' // Surplus
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Tooltip from '@mui/material/Tooltip'
import ConstructionIcon from '@mui/icons-material/Build'
import BroadbandIcon from '@mui/icons-material/NetworkCheck'
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf'
import ConservationIcon from '@mui/icons-material/Nature'
import ButtonGroup from '@mui/material/ButtonGroup'

function escapeRegExp(value: string): string {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}

const mergeData = (data, industryJobs, categoryData) => {
  return data.map((row, index) => {
    const categoryInfo = categoryData.find((category) => category.SOC === row.SOC) || {}
    const industryJobInfo = industryJobs.find((job) => job.SOC === row.SOC) || {}
    return {
      ...row,
      ...industryJobInfo,
      ...categoryInfo,
      id: index + 1,
    }
  })
}

// Utility function for filtering data by category
const filterDataByCategory = (mergedData, selectedCategory) => {
  if (selectedCategory === 'All Categories') {
    return mergedData
  }

  return mergedData.filter((row) => {
    const categoryValue = parseFloat(row[selectedCategory])
    return !isNaN(categoryValue) && categoryValue === 1.0
  })
}
// Define custom styles for the selected and default buttons

export default function OccupationsTable() {
  const theme = createTheme({
    typography: {
      fontSize: 16,
    },
  })
  const headerHeight = 100 // Adjust this value based on your actual header height
  const totalJobs = 307362 // Total jobs for the calculation
  // Calculate the available height for the DataGrid
  const dataGridHeight = `calc(100vh - ${headerHeight}px)`
  const [data, setData] = React.useState<any[]>([])
  const [searchText, setSearchText] = React.useState('')
  const [rows, setRows] = React.useState<any[]>([])
  const [industryJobs, setIndustryJobs] = React.useState<any[]>([])
  const [categoryData, setCategoryData] = React.useState<any[]>([])
  const [selectedCategory, setSelectedCategory] = React.useState('All Categories')
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    {
      field: 'IIJA Demand',
      sort: 'desc' as GridSortDirection,
    },
  ])
  const [open, setOpen] = useState(false)

  const handleClickOpen = () => {
    setOpen(true)
  }

  // Handler for category button click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category)
  }

  const handleClose = () => {
    setOpen(false)
  }

  // Handler for 'All Occupations' button click
  const handleAllOccupationsClick = () => {
    setSelectedCategory('All Categories')
  }

  // Define the categories
  const IIJACategories = ['Construction', 'Broadband', 'Clean Energy', 'Conservation']
  const IRACategories = [
    'IRA Clean Energy',
    'IRA Clean Manufacturing',
    'IRA Efficient Building Jobs',
    'IRA Environmental Justice Jobs',
  ]

  const selectedButtonStyle = {
    backgroundColor: '#4caf50', // Green color for selected button
    color: 'white',
  }

  const defaultButtonStyle = {
    borderColor: '#4caf50', // Green color for border of default button
    color: '#4caf50',
  }

  // Function to determine button variant
  const getButtonVariant = (category) => {
    return selectedCategory === category ? 'contained' : 'outlined'
  }

  // Function to determine button style
  const getButtonStyle = (category) => {
    return selectedCategory === category ? selectedButtonStyle : defaultButtonStyle
  }

  const CustomHeader = ({title, description}) => {
    const [open, setOpen] = useState(false)

    const handleClickOpen = (e) => {
      e.stopPropagation() // Stop event propagation
      setOpen(true)
    }

    const handleClose = () => {
      setOpen(false)
    }

    const getDialogContentText = (title) => {
      const sources = (
        <>
          <hr />
          <h5>Sources:</h5>
          <ul>
            <li>Lightcast 2024 Data Pull</li>
            <li>Industry Experts</li>
            <li>BLI</li>
            <li>The Associated General Contractors of America</li>
          </ul>
        </>
      )

      if (title === 'Estimated Demand') {
        return (
          <>
            <p>
              Our estimate for demand is determined through a <strong>two-stage approach</strong>,
              combining quantitative assessment and strategic allocation. This is additional demand
              due to IIJA / IRA funding, not year over year demand.
            </p>
            <ul>
              <li>
                <strong>Quantitative Assessment:</strong>
                <p>
                  First, we utilize the IMPLAN Input-Output Model to quantify the economic impact of
                  infrastructure investments, with a primary focus on{' '}
                  <strong>total employment by funding as a key metric</strong>.
                </p>
              </li>
              <li>
                <strong>Strategic Allocation:</strong>
                <p>
                  Second, we apply a <strong>proportional distribution methodology</strong> to
                  allocate anticipated job roles to relevant IIJA / IRA sectors. This strategy is
                  based on the assumption of maintaining
                  <strong>consistent worker-to-role ratios</strong>, corroborated by established
                  industry standards. This assumption has been validated through extensive
                  consultation with industry experts.
                </p>
              </li>
            </ul>
            <p>
              To arrive at our estimates, we initially calculate the total construction and
              IIJA-related job count within a budget range of $5-7 billion. We use the AGC's
              construction budget formula to determine the additional jobs required. These jobs are
              then allocated based on the proportion of jobs already existing within the relevant
              IIJA industries. We assume that worker ratios will remain consistent for IIJA
              projects, similar to other comparable projects in these industries.
            </p>
            <p>
              It's important to note that our estimate is currently based on a{' '}
              <strong>$7 billion budget projection</strong>, providing a reliable and adaptable
              framework for understanding labor market shifts resulting from IIJA /IRA initiatives.
            </p>
            {sources}
          </>
        )
      } else if (title === 'Difficult to Fill') {
        return (
          <>
            <p>
              The <strong>Difficult to Fill Index</strong> serves as a quantitative indicator
              designed to measure the intensity of hiring activities, thereby providing an
              approximate gauge for the challenges faced by employers in filling job vacancies. This
              ratio is calculated by comparing the total number of job openings to the actual number
              of successful hires across all industry sectors. <br />
              <br /> A low index value suggests a dynamic labor market characterized by{' '}
              <strong>vigorous hiring activity</strong>, which frequently surpasses the volume of
              available job openings due to factors such as workforce attrition and retirement.
              Conversely, a high index value implies that employers encounter fewer difficulties in
              staffing positions as they become vacant.
            </p>
            {sources}
          </>
        )
      } else if (title === 'Need for Training') {
        return (
          <>
            <p>
              The <strong>Training Gap Index</strong> is an analytical measure designed to quantify
              the disparity between the supply of skilled labor and the market's demand for it. This
              index incorporates not just the number of job openings and regional educational
              completions but also the{' '}
              <strong>additional demand projected due to IIJA / IRA funding</strong>.<br />
              <br /> In a scenario where educational completions outpace the sum of job openings and
              IIJA/IRA-induced demand, the market experiences a <strong>skill surplus</strong>.
              Conversely, if the sum of job openings and IIJA/IRA-induced demand surpasses the
              number of educational completions, a significant
              <strong> Training Gap</strong> manifests, signifying a critical shortage of qualified
              personnel.
            </p>
            {sources}
          </>
        )
      }
      return '' // Default
    }

    return (
      <div>
        <IconButton
          aria-label='info'
          size='medium'
          onClick={handleClickOpen}
          style={{color: '#16384a'}}
        >
          <HelpOutlineIcon fontSize='inherit' />
        </IconButton>
        {title}
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{`${title} Explanation`}</DialogTitle>
          <DialogContent>
            <DialogContentText>{getDialogContentText(title)}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color='primary'>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }

  React.useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + '/occupations_iija')
      .then((response) => {
        setData(response.data)
      })
      .catch((error) => {
        console.error('There was an error!', error)
      })
  }, [])

  // Removed the useEffect for fetching categories as it's no longer required

  React.useEffect(() => {
    const processedData = data.map((row, index) => {
      return {
        ...row,
        id: index + 1,
        // Assuming IIJA Demand is a direct field in your new data
        IIJADemand: row.IIJADemand, // Update this line as per the actual field name
      }
    })

    setRows(processedData)
  }, [data])

  // Fetch the iija_categories table data

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + '/iija_categories')
      .then((response) => {
        setCategoryData(response.data)
      })
      .catch((error) => {
        console.error('There was an error!', error)
      })
  }, [])

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + '/staffing-patterns-all-categories')
      .then((response) => {
        setIndustryJobs(response.data)
      })
      .catch((error) => {
        console.error('There was an error!', error)
      })
  }, [])

  // Merging and filtering data
  useEffect(() => {
    const mergedData = mergeData(data, industryJobs, categoryData)
    console.log('Merged Data:', mergedData)

    const filteredData = filterDataByCategory(mergedData, selectedCategory)
    console.log('Filtered Data:', filteredData)

    setRows(filteredData)
  }, [data, industryJobs, categoryData, selectedCategory])

  const getIIJADemandClass = (params) => {
    const iijaDemand = params.value
    if (iijaDemand >= 3000) return 'cell-bg-red'
    if (iijaDemand >= 500) return 'cell-bg-orange'
    if (iijaDemand >= 100) return 'cell-bg-yellow'
    if (iijaDemand >= 50) return 'cell-bg-lightgreen'
    return 'cell-bg-green'
  }

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue)
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i')
    const filteredRows = data
      .map((row, index) => ({
        ...row,
        id: index + 1,
      }))
      .filter((row: any) => {
        return Object.keys(row).some((field: any) => {
          if (row[field] !== null && row[field] !== undefined) {
            return searchRegex.test(row[field].toString())
          } else {
            return false
          }
        })
      })
    setRows(filteredRows)
  }

  const columns = [
    {field: 'SOC', headerName: 'SOC', flex: 0.7, type: 'string'},
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      type: 'string',
      renderCell: (params) => {
        const value = params.value as string
        return <div style={{fontWeight: 'bold', fontSize: 'larger'}}>{value}</div>
      },
    },
    // {
    //   field: 'categoryIcons',
    //   headerName: 'Categories',
    //   flex: 0.7,
    //   sortable: false,
    //   disableExport: true,
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         {params.row.construction === 1 && (
    //           <Tooltip title='Construction Category'>
    //             <ConstructionIcon style={{color: '#f44336'}} />
    //           </Tooltip>
    //         )}
    //         {params.row.broadband === 1 && (
    //           <Tooltip title='Broadband Category'>
    //             <BroadbandIcon style={{color: '#2196F3'}} />
    //           </Tooltip>
    //         )}
    //         {params.row.clean_energy === 1 && (
    //           <Tooltip title='Clean Energy Category'>
    //             <EnergySavingsLeafIcon style={{color: '#4CAF50'}} />
    //           </Tooltip>
    //         )}
    //         {params.row.conservation === 1 && (
    //           <Tooltip title='Conservation Category'>
    //             <ConservationIcon style={{color: '#FFC107'}} />
    //           </Tooltip>
    //         )}
    //       </>
    //     )
    //   },
    // },

    {
      field: '2022 Jobs',
      headerName: '2022 Total Jobs',
      flex: 0.7,
      type: 'number',
      align: 'center' as 'center',
    },
    {
      field: 'IIJA Demand',
      renderHeader: (params) => {
        return (
          <CustomHeader
            title='Estimated Demand'
            description='This column shows the estimated demand based on the IIJA program.'
          />
        )
      },
      headerName: 'Estimated Demand',
      align: 'center' as GridAlignment, // Explicitly cast to GridAlignment
      headerAlign: 'center' as GridAlignment, // Explicitly cast to GridAlignment
      flex: 1.2,
      type: 'number',
      valueGetter: (params) => {
        const totalEmployed = params.row['Total Employed in 2022']
        const denominator = 152768 // This can be adjusted based on your specific formula
        let iijaDemand = 0

        if (totalEmployed !== null && totalEmployed !== undefined) {
          iijaDemand = (totalEmployed / denominator) * 30550 // Adjust this formula as needed
        }

        return Math.round(iijaDemand)
      },
      valueFormatter: (params) => {
        const value = params.value as number
        if (value === 0) {
          return 'TBD*'
        }
        return value !== undefined ? value.toLocaleString() : 'N/A'
      },
      renderCell: (params) => {
        let value = params.formattedValue as string
        // if (params.row.SOC === '49-9021') {
        //   value = '2745'
        // }
        // if (params.row.SOC === '47-2111') {
        //   value = '4284'
        // }
        if (params.row.SOC === '47-4011.01') {
          value = '125-210*'
        }
        if (value === 'TBD*') {
          return <div style={{fontWeight: 'bold', fontSize: 'larger', color: 'red'}}>{value}</div>
        }
        return <div style={{fontWeight: 'bold', fontSize: 'larger'}}>{value}</div>
      },
      cellClassName: getIIJADemandClass, // Use your existing logic to assign class names based on the value
    },

    {
      field: 'Demand',
      renderHeader: (params) => {
        return (
          <CustomHeader
            title='Difficult to Fill'
            description='This column shows the info about Difficult to fill.'
          />
        )
      },

      headerName: 'Difficulty to Fill',
      flex: 1.2,
      headerAlign: 'center' as 'center',
      type: 'number',
      align: 'center' as 'center',
      disableExport: true,
      valueGetter: (params) => {
        const jobs2022 = params.row['2022 Jobs']
        const hires = params.row['2022 Hires']
        return jobs2022 && hires ? Math.round((jobs2022 / hires) * 100) : 'N/A'
      },
      renderCell: (params) => {
        let IconComponent: React.ElementType | null = null
        let label = 'Unknown' // Default label
        let color = 'gray' // Default color
        const value = params.value as number
        const jobs2022 = params.row['2022 Jobs']
        const hires = params.row['2022 Hires']
        const formulaText = `(2022 Jobs ${jobs2022} / 2022 Hires ${hires}) * 100 = ${value}`

        if (value <= 120) {
          IconComponent = VeryHighIcon
          label = `Very High`
          color = '#c3002f' // Red
        } else if (value <= 190) {
          IconComponent = HighIcon
          label = `High`
          color = 'Darkorange' // Yellow
        } else if (value <= 240) {
          IconComponent = MediumIcon
          label = `Medium`
          color = 'Orange' // Blue
        } else if (value <= 300) {
          IconComponent = LowIcon
          label = `Low`
          color = 'Blue' // Purple
        } else if (value <= 337) {
          IconComponent = VeryLowIcon
          label = `Very Low`
          color = '#245d38' // Tree Green
        }

        return (
          <Tooltip
            title={
              <React.Fragment>
                <Typography variant='body2' style={{fontSize: '1.5em'}}>
                  <div style={{marginBottom: '5px'}}>
                    <strong>Formula:</strong>
                  </div>
                  <div style={{marginLeft: '10px'}}>2022 Jobs: {jobs2022}</div>
                  <div style={{marginLeft: '10px'}}>2022 Hires: {hires}</div>
                  <div style={{marginTop: '5px', marginLeft: '10px'}}>
                    <strong>Result:</strong> {value}
                  </div>
                </Typography>
              </React.Fragment>
            }
            arrow
          >
            <Chip
              icon={IconComponent ? <IconComponent style={{color}} /> : undefined}
              label={label}
              style={{backgroundColor: 'transparent', border: '1px solid', color: color}}
              size='small'
            />
          </Tooltip>
        )
      },
    },
    {
      field: 'Training Gap',
      headerName: 'Need for Training',
      headerAlign: 'center' as 'center',
      align: 'center' as 'center',
      flex: 1.2,
      renderHeader: (params) => {
        return (
          <CustomHeader
            title='Need for Training'
            description='This column shows the info about Need for training.'
          />
        )
      },
      type: 'number',

      valueGetter: (params) => {
        const completions = params.row['Regional Completions (2021)']
        const openings = params.row['Avg. Annual Openings']

        const denominator = 152768
        let iijaDemand = 0

        iijaDemand = Math.round(iijaDemand) // Round it off

        if (completions === 0) {
          return 0
        }
        // Inside valueGetter

        // Include iijaDemand in the calculation
        return Math.round(openings + iijaDemand - completions)
      },

      renderCell: (params) => {
        let iijaDemand = params.row._iijaDemand || 0
        let IconComponent: React.ElementType | null = null
        let label = 'Unknown' // Default label
        let color = 'gray' // Default color
        const completions = params.row['Regional Completions (2021)']
        const openings = params.row['Avg. Annual Openings']

        const value = params.value as number // assuming value is the difference between jobs and graduates

        if (value > 0) {
          if (value > 4000) {
            IconComponent = ErrorIcon
            label = 'Critical Need'
            color = '#c3002f' // Deep Red
          } else if (value > 1000) {
            IconComponent = WarningIcon
            label = 'High Need'
            color = '#ff8800' // Orange
          } else if (value > 500) {
            IconComponent = InfoIcon
            label = 'Moderate Need'
            color = '#001970' // Blue
          } else {
            // value > 25 and value <= 500
            IconComponent = CheckCircleIcon
            label = 'Low Need'
            color = '#00aaff' // Light Blue
          }
        } else if (value < 0) {
          // Surplus scenario
          IconComponent = VerifiedIcon
          label = 'Surplus'
          color = '#245d38' // Deep Green
        } else {
          // value > 0 and value <= 25
          IconComponent = DoneAllIcon
          label = 'Adequate'
          color = '#00aa00' // Green
        }
        if (params.row['Typical On-The-Job Training'] === 'Apprenticeship') {
          IconComponent = ErrorIcon
          label = 'High Need: Apprenticeship'
          color = 'Red'
        }
        if (completions === 0) {
          IconComponent = VerifiedIcon
          label = 'Insufficient Data'
          color = 'black' // Deep Green
        }
        if (
          params.row['Typical On-The-Job Training'] === 'Short-term on-the-job training' ||
          params.row['Typical On-The-Job Training'] === 'Moderate-term on-the-job training' ||
          params.row['Typical On-The-Job Training'] === 'Long-term on-the-job training'
        ) {
          IconComponent = ErrorIcon
          label = 'None: On the Job Training'
          color = 'Teal'
        }
        if (params.row.SOC === '47-1011') {
          IconComponent = ErrorIcon
          label = 'None: Needs Work Experience'
          color = 'Teal'
        }
        if (params.row.SOC === '47-4011.01') {
          IconComponent = ErrorIcon
          label = 'High Need: Training'
          color = 'Red'
        }
        if (params.row.SOC === '49-9021') {
          IconComponent = ErrorIcon
          label = 'High Need: Apprenticeship'
          color = 'DarkRed'
          iijaDemand = 2745
        }
        if (params.row.SOC === '47-4011.01') {
          IconComponent = ErrorIcon // Assuming ErrorIcon is used to indicate attention or highlight
          label = 'High Need: Significant Growth Forecast'
          color = 'Red' // Or choose a color that visually complements the message
          // Custom message for the tooltip
          const customMessage =
            'Forecasted Increase in Training Demand: Over the next decade, initiatives and advancements in Colorado are expected to significantly boost the need for specialized training. This surge is driven by new programs promoting clean energy enhancements.'

          return (
            <Tooltip
              title={
                <React.Fragment>
                  <Typography variant='body2' style={{fontSize: '1.5em'}}>
                    <div style={{marginBottom: '5px'}}>
                      <strong>Insight:</strong>
                    </div>
                    <div style={{marginLeft: '10px'}}>{customMessage}</div>
                  </Typography>
                </React.Fragment>
              }
              arrow
            >
              <Chip
                icon={IconComponent ? <IconComponent style={{color}} /> : undefined}
                label={`${label}`}
                style={{backgroundColor: 'transparent', border: '1px solid', color: color}}
                size='small'
              />
            </Tooltip>
          )
        }

        return (
          <Tooltip
            title={
              <React.Fragment>
                <Typography variant='body2' style={{fontSize: '1.5em'}}>
                  <div style={{marginBottom: '5px'}}>
                    <strong>Formula:</strong>
                  </div>
                  <div style={{marginLeft: '10px'}}>Annual Openings: {openings}</div>
                  <div style={{marginLeft: '10px'}}>Annual Regional Completions: {completions}</div>
                  <div style={{marginTop: '5px', marginLeft: '10px'}}>
                    <strong>Result:</strong> {value}
                  </div>
                </Typography>
              </React.Fragment>
            }
            arrow
          >
            <Chip
              icon={IconComponent ? <IconComponent style={{color}} /> : undefined}
              label={`${label}: ${value}`}
              style={{backgroundColor: 'transparent', border: '1px solid', color: color}}
              size='small'
            />
          </Tooltip>
        )
      },
      sortComparator: (v1, v2, cellParams1, cellParams2) => v1 - v2,
    },
  ]

  // Importing Link from react-router-dom for the button link.

  const getDetailPanelContent = ({row}) => (
    <Paper elevation={3} style={{padding: '20px', margin: '20px', backgroundColor: '#f5f5f5'}}>
      <Card elevation={0}>
        <CardContent>
          <Typography
            variant='h5'
            component='div'
            gutterBottom
            style={{fontWeight: 'bold', color: '#16384a'}}
          >
            📊 Occupation Details
          </Typography>
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <div>
              {[
                [
                  '💰 Median Annual Earnings:',
                  `$${Number(row['Median Annual Earnings']).toLocaleString()}`,
                ],
                ['📉 2022-2027 % Change:', `${Math.round(row['2022 - 2027 % Change'] * 100)}%`],
                ['📆 Avg. Annual Openings:', Number(row['Avg. Annual Openings']).toLocaleString()],
              ].map(([title, value], index) => (
                <div key={index} style={{display: 'flex', alignItems: 'center'}}>
                  <Typography variant='h6' color='textSecondary' style={{fontWeight: 'bold'}}>
                    {title}
                  </Typography>
                  <Typography variant='body1' style={{color: '#999999', marginLeft: '8px'}}>
                    {value}
                  </Typography>
                </div>
              ))}
            </div>
            <div>
              {[
                ['🤝 2022 Hires:', Number(Math.round(row['2022 Hires'])).toLocaleString()],
                [
                  '👋 2022 Separations:',
                  Number(Math.round(row['2022 Separations'])).toLocaleString(),
                ],
                // ['🔄 2022 Turnover Rate:', `${row['2022 Turnover Rate']}%`],
                [
                  '🎓 Regional Completions (2021):',
                  Number(row['Regional Completions (2021)']).toLocaleString(),
                ],
              ].map(([title, value], index) => (
                <div key={index} style={{display: 'flex', alignItems: 'center'}}>
                  <Typography variant='h6' color='textSecondary' style={{fontWeight: 'bold'}}>
                    {title}
                  </Typography>
                  <Typography variant='body1' style={{color: '#999999', marginLeft: '8px'}}>
                    {value}
                  </Typography>
                </div>
              ))}
            </div>
            <div>
              {[
                ['👴 Current Year Age 65+:', Number(row['Current Year Age 65+']).toLocaleString()],
                ['🛠️ Typical On the Job Training:', row['Typical On-The-Job Training']],
                ['💼 Work Experience Required:', row['Work Experience Required']],
                ['🎓 Typical Entry Level Education:', row['Typical Entry Level Education']],
              ].map(([title, value, index]) => (
                <div key={index} style={{display: 'flex', alignItems: 'center'}}>
                  <Typography variant='h6' color='textSecondary' style={{fontWeight: 'bold'}}>
                    {title}
                  </Typography>
                  <Typography variant='body1' style={{color: '#999999', marginLeft: '8px'}}>
                    {value}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          {/* <div style={{textAlign: 'center', marginTop: '20px'}}>
            <Button variant='contained' color='primary'>
              <Link
                to='https://luminancedata.com/iija/commuter_map'
                target='_blank'
                style={{color: 'white'}}
              >
                Resident Workers and Net Commuters Map
              </Link>
            </Button>
          </div> */}
        </CardContent>
      </Card>
    </Paper>
  )

  return (
    <ThemeProvider theme={theme}>
      <div className='card'>
        <Typography variant='h3' align='center' gutterBottom>
          IIJA / IRA Occupation Demand
        </Typography>
        {/* All Occupations Button */}
        <div style={{textAlign: 'left', marginBottom: '10px'}}>
          <Button
            variant={getButtonVariant('All Categories')}
            style={getButtonStyle('All Categories')}
            onClick={handleAllOccupationsClick}
          >
            All Occupations
          </Button>
        </div>
        {/* Category Filter Buttons */}
        <div>
          {/* IIJA Occupations */}
          <Typography variant='h6'>IIJA Occupations</Typography>
          <ButtonGroup aria-label='IIJA occupations button group'>
            {IIJACategories.map((category) => (
              <Button
                key={category}
                variant={getButtonVariant(category)}
                style={getButtonStyle(category)}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </Button>
            ))}
          </ButtonGroup>

          {/* IRA Occupations */}
          <Typography variant='h6' style={{marginTop: '10px'}}>
            IRA Occupations
          </Typography>
          <ButtonGroup aria-label='IRA occupations button group'>
            {IRACategories.map((category) => (
              <Button
                key={category}
                variant={getButtonVariant(category)}
                style={getButtonStyle(category)}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <br />
        <DataGridPro
          rows={rows}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={({row}) => 300}
          columns={columns}
          components={{
            Toolbar: GridToolbar,
          }}
          className='datagrid'
          getRowClassName={(params) => (Number(params.id) % 2 === 0 ? 'even-row' : 'odd-row')}
          style={{height: dataGridHeight}} // Set the height to occupy the remaining spac
          sortModel={sortModel}
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
        />
        {/* Your new code starts here */}
        <br />
        <br />
        <div style={{textAlign: 'center'}}>
          <Button
            variant='outlined'
            color='primary'
            onClick={handleClickOpen}
            style={{fontSize: 'larger'}} // Increase the font size
          >
            Data Source Information
          </Button>
          <IconButton color='info' onClick={handleClickOpen}>
            <InfoIcon />
          </IconButton>
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Data Notes and Sources</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {' '}
              <Typography variant='body1' style={{padding: '0 20px', textAlign: 'left'}}>
                <strong>IIJA / IRA Occupations Table</strong>
                <br />
                <br />
                This table provides important information about IIJA/IRA-related occupations,
                including details about their demand, growth prospects, and the training required
                for these roles.
                <br />
                <br />
                <strong>Note:</strong> Hover over the (?) information icons for more details about
                each column. Hover over "Difficult to Fill" and "Need for Training" Buttons to view
                specific data and formula.
                <br />
                <br />
                <strong>SECTION SOURCES:</strong>
                <ul>
                  <li>
                    Lightcast Labor Analytics Data:{' '}
                    <a href='https://lightcast.io/' target='_blank' rel='noopener noreferrer'>
                      https://lightcast.io/
                    </a>
                  </li>
                  <li>US Bureau of Labor Statistics (BLS)</li>
                  <li>
                    Workforce calculator provided by the Association of General Contractors (AGC)
                  </li>
                  <li>DORA - Colorado Department of Regulatory Agencies</li>
                  <li>
                    Luminance Data Proprietary Indices (Estimated Demand, Difficult to Fill, Need
                    for Training, Conservation, and hand coding of SOC codes for IIJA/IRA projects
                    in Colorado)
                  </li>
                </ul>
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color='primary'>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Typography variant='body2' align='center' gutterBottom style={{marginTop: '1em'}}>
          Lightcast Labor Market Data (2024) // BLS.gov - Invest.gov (2024). Investing in America
          Map. Retrieved from
          https://www.whitehouse.gov/wp-content/uploads/2023/11/Invest.gov_PublicInvestments_Map_Data_CURRENT.xlsx.
        </Typography>
      </div>
    </ThemeProvider>
  )
}
